import {
  employeesShortNamesAndI,
  employeesWithTimekeepingPeriods,
  getOrganization,
  writeAccess,
} from 'src/store/selectors';
import { calculateSicknessAbsenceStats } from 'src/utilities/sickness-absence-stats-selector';
import { calculateBalance } from 'src/utilities/time-balance/time-balance';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date';
import type { ApiClient } from 'src/store/backend';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import { assertIsDefined } from '../../assertIsDefined';
import type {
  AbsenceSummaryData,
  EmployeeMonthView,
  MonthlyHoursSummaryData,
  SickLeaveSummaryData,
  SummaryPayload,
  SummaryView,
  TimeBalanceSummaryData,
} from '../../content/summaries/summary-view';
import type { EmployeeTimekeeping } from '../../pages/staffing-page/employee-timekeeping';
import { sortBy } from 'lodash';
import { pageView } from '../pages/page-view';
import { pageIds } from '../../store/selectors/pages/pageIds';
import { getPageGroups } from '../../store/selectors/pages/getPageGroups';
import { t, tArray } from 'src/i18n';

export async function buildSummaryView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<SummaryView> {
  const pages = getPageGroups(state);
  const staffingPage = await pageView(api, '', pageIds.staffing, pages, state);

  if (staffingPage.type !== 'staffing-page') {
    throw new Error('Illegal state wrong page type');
  }

  const organization = getOrganization(state);
  assertIsDefined(organization);

  // Check if user has extended staffing access
  if (!staffingPage.currentUserHasExtendedStaffingAccess) {
    const payload: SummaryPayload = {
      subType: 'no-staffing-access',
      message: t('summaries.noStaffingAccess'),
    };

    return {
      ...commonData,
      docsForLinking: [],
      currentUserHasAccess: true,
      currentUserHasWriteAccess: writeAccess(state),
      attachments: [],
      helpContent: '',
      name: t('summaries.missingAccess'),
      today: LocalDate.fromString(state.today),
      type: 'summaries',
      uuid: uuid,
      isConfirmedEntity: true,
      href: currentParent + '/summaries/' + uuid,
      pdfHref: '',
      parentHref: currentParent,
      deleted: false,
      deletable: true,
      hasDraft: false,
      fetchDraft: () => Promise.reject(new Error('No draft')),
      payload,
    };
  }

  // Parse uuid to determine summary type
  const parts = uuid.split(':');
  const summaryType = parts[0];

  let payload: SummaryPayload;
  let viewName: string;

  switch (summaryType) {
    case 'monthly-hours': {
      // Build monthly-hours summary data
      const availableEmployees = sortBy(
        employeesShortNamesAndI(state)
          .filter((e) => e.name !== '')
          .filter((e) => e.status !== 'TERMINATED')
          .map((p) => ({ value: p.uuid, text: p.name })),
        ['text'],
      );

      // Generate years from 2020 to current year
      const today = LocalDate.fromString(state.today);
      const currentYear = today.year();
      const startYear = 2020;
      const availableYears: { value: string; text: string }[] = [];
      for (let year = currentYear; year >= startYear; year--) {
        availableYears.push({ value: year.toString(), text: year.toString() });
      }

      const monthNames = tArray('library.selectDate.months');

      // Parse selection: employee:year:month
      const selectedEmployee = parts[1] ?? '';
      const selectedYear = parts[2] ? parseInt(parts[2]) : currentYear;
      const selectedMonth = parts[3] ?? '';

      // Generate months for selected year (or all years if none selected)
      const availableMonths: { value: string; text: string }[] = [];
      for (let month = 1; month <= 12; month++) {
        const monthStr = month.toString().padStart(2, '0');
        const value = `${monthStr}`;
        const text = monthNames[month - 1];
        availableMonths.push({ value, text });
      }

      const selectedYearMonth = selectedYear && selectedMonth ? `${selectedYear}-${selectedMonth}` : '';

      let selectedEmployeeMonth: EmployeeMonthView | undefined = undefined;
      const e = availableEmployees.find((x) => x.value === selectedEmployee);
      const ym = availableMonths.find((x) => x.value === selectedYearMonth);

      viewName = t('summaries.monthlyHoursTitle');

      if (e) {
        viewName += ': ' + e.text;
      }

      if (ym) {
        viewName += ', ' + ym.text;
      }

      if (selectedEmployee !== '' && selectedYearMonth !== '') {
        assertIsDefined(e);

        // Find employee data in staffing calendar
        const d = staffingPage.calendarData.data.flatMap((v) => v.employees).find((x) => x.uuid === selectedEmployee);

        // Build selectedEmployeeMonth with days if found, otherwise empty array
        const days = d ? d.days.filter((x) => x.date.startsWith(selectedYearMonth)) : [];

        selectedEmployeeMonth = {
          name: e.text,
          employeeUuid: e.value,
          yearMonth: selectedYearMonth,
          days: days,
        };
      }

      const data: MonthlyHoursSummaryData = {
        availableEmployees,
        availableYears,
        availableMonths,
        selectedEmployee: selectedEmployee !== '' ? selectedEmployee : undefined,
        selectedYear: selectedYear,
        selectedYearMonth: selectedYearMonth !== '' ? selectedYearMonth : undefined,
        selectedEmployeeMonth,
      };

      payload = { subType: 'monthly-hours', data };
      break;
    }

    case 'time-balance': {
      // Build time-balance summary data (Plusstid og avspasering)
      viewName = t('summaries.timeBalanceTitle');

      const employeesWithPeriods: EmployeeTimekeeping[] = employeesWithTimekeepingPeriods(state).map((e) => {
        return {
          uuid: e.uuid,
          name: e.name,
          saldo: calculateBalance(e.periods, e.forStaffing.employmentStartDate, e.forStaffing.employmentEndDate),
          periods: e.periods.map((l) => ({
            label: l.timeDisplay,
            accessible: true,
            rightLabel: l.hoursDisplay,
            updateStatus: 'none',
            clickData: JSON.stringify(l),
          })),
          forStaffing: e.forStaffing,
        };
      });

      const data: TimeBalanceSummaryData = {
        employeeGroups: staffingPage.employeeGroups,
        employeesWithPeriods,
        defaultDate: staffingPage.today.toString(),
        timekeepingBeforeEmploymentStartWarning: staffingPage.timekeepingBeforeEmploymentStartWarning,
      };

      payload = { subType: 'time-balance', data };
      break;
    }

    case 'absence': {
      // Build absence summary data (Fraværsstatistikk)
      viewName = t('summaries.absenceTitle');

      const data: AbsenceSummaryData = {
        // Note: StaffingStatisticsEmployee interface expects leavePeriods, displayName, uuid
        // EmployeeForStaffingCalendar has all these properties, so we can use it directly
        employees: staffingPage.employeesForStaffingCalendar as any,
      };

      payload = { subType: 'absence', data };
      break;
    }

    case 'sick-leave': {
      // Build sick-leave summary data (Sykefraværsstatistikk)
      viewName = t('summaries.sickLeaveTitle');

      const data: SickLeaveSummaryData = {
        sicknessAbsenceStats: calculateSicknessAbsenceStats(state),
        employees: staffingPage.employeesForStaffingCalendar,
      };

      payload = { subType: 'sick-leave', data };
      break;
    }

    default: {
      // Unknown summary type
      viewName = t('summaries.unknownTitle');
      payload = {
        subType: 'not-found',
        message: t('summaries.notFound', { type: summaryType }),
      };
      break;
    }
  }

  return {
    ...commonData,
    docsForLinking: [],
    currentUserHasAccess: true,
    currentUserHasWriteAccess: writeAccess(state),
    attachments: [],
    helpContent: '',
    name: viewName,
    today: LocalDate.fromString(state.today),
    type: 'summaries',
    uuid: uuid,
    isConfirmedEntity: true,
    href: currentParent + '/summaries/' + uuid,
    pdfHref: '',
    parentHref: currentParent,
    deleted: false,
    deletable: true,
    hasDraft: false,
    fetchDraft: () => Promise.reject(new Error('No draft')),
    payload,
  };
}
